import z from 'zod';

import type { EncodeInput, FieldTransformer, PartialEncodeInput, TransformerConfig } from './types';

/**
 * Simplifies a complex type by flattening it to its actual shape.
 * This forces TypeScript to evaluate the type and show the actual fields.
 */
type Simplify<T> = { [K in keyof T]: T[K] } & {};

/**
 * Converts an array to a Zod mask consisting of the array's values as keys with `true` as the value.
 */
export const arrayToMask = <T extends readonly string[]>(arr: T): { [K in T[number]]: true } => {
  return Object.fromEntries(arr.map(key => [key, true])) as any;
};

/**
 * Type-safe pick that returns Pick<T, K> instead of lodash's Partial<T>.
 *
 * Lodash's _.pick() has two overloads:
 *   1. _.pick(obj, ...keys: U[])  -> Pick<T, U>    (requires U extends keyof T)
 *   2. _.pick(obj, ...PropertyPath[]) -> Partial<T>  (fallback)
 *
 * In practice, overload 1 fails and falls back to Partial<T> when:
 *   - Entity types are nullable (T | null from ElectroDB's getById), so keyof (T | null) = never
 *   - Entity types are complex intersections/unions from ElectroDB that don't satisfy the constraint
 *   - Attribute constants contain keys from the DB schema that don't exist on the client TS type
 *     (e.g. codec-transformed fields like startDatetimeIdentifier -> startDatetime)
 *
 * This function uses K extends string (not keyof T) to bypass these constraints, and
 * Extract<K, keyof T> in the return type to produce the correct narrowed Pick type.
 */
export const pick = <T extends object, K extends string>(
  obj: T,
  keys: readonly K[]
): Pick<T, Extract<K, keyof T>> => {
  return Object.fromEntries(
    keys.filter(key => key in obj).map(key => [key, (obj as Record<string, unknown>)[key]])
  ) as Pick<T, Extract<K, keyof T>>;
};

/**
 * Converts an object to a Zod mask consisting of the object's keys with `true` as the value.
 * Optional keys are kept optional in the mask.
 */
export const objectToMask = <T extends object>(obj: T): { [K in keyof T]: true } => {
  return Object.fromEntries(Object.keys(obj).map(key => [key, true])) as any;
};

/**
 * Fetches the intersection of multiple masks, while maintaining type safety.
 * All masks must have value `true` for it to be included in the result.
 */
export const intersectMasks = <T extends Record<string, boolean>[]>(
  ...masks: T
): { [K in keyof T[number]]: true } => {
  if (masks.length === 0) return {} as any;
  if (masks.length === 1) {
    // For single mask, only include keys with true values
    const mask = masks[0]!;
    return Object.fromEntries(Object.entries(mask).filter(([_, value]) => value === true)) as any;
  }

  // Collect all keys that have true value in at least one mask
  const allKeys = new Set<string>();
  for (const mask of masks) {
    for (const [key, value] of Object.entries(mask)) {
      if (value === true) {
        allKeys.add(key);
      }
    }
  }

  // Filter to keys that have true value in ALL masks
  const intersectionKeys = Array.from(allKeys).filter(key =>
    masks.every(mask => mask[key] === true)
  );

  return Object.fromEntries(intersectionKeys.map(key => [key, true])) as any;
};

/** Measures the size of a JSON object in bytes. */
export const getObjectSizeInBytes = (obj: object) =>
  // Built per call, so evaluating the module needs no encoder.
  new TextEncoder().encode(JSON.stringify(obj)).length;

/**
 * Builds a partial schema covering the fields that are both masked and present in
 * the target. Returns `undefined` when that intersection is empty.
 */
const getMaskedSchema = <TSchema extends z.ZodObject<any>>(params: {
  schema: TSchema;
  maskedKeys: readonly (keyof z.infer<TSchema>)[];
  target: Record<string, any>;
  declaredOnly?: boolean;
}): z.ZodObject<any> | undefined => {
  const { schema, maskedKeys, target, declaredOnly = false } = params;

  // Convert masked keys array to a mask
  const mask = arrayToMask(maskedKeys as readonly string[]);

  // Find the intersection of masked keys and keys present in target data. A read also
  // narrows to the keys the schema declares, because pick() throws on anything else; a
  // write must keep that throw, so a field named for the wrong schema is not dropped.
  const targetMask = objectToMask(target);
  const fieldsToValidateMask = declaredOnly
    ? intersectMasks(mask, targetMask, objectToMask(schema.shape as Record<string, unknown>))
    : intersectMasks(mask, targetMask);

  if (Object.keys(fieldsToValidateMask).length === 0) {
    return undefined;
  }

  return schema.pick(fieldsToValidateMask).partial();
};

/**
 * Validates and filters masked fields from target data against a schema.
 *
 * This utility ensures that:
 * 1. Only fields that are included in the mask (maskedKeys) are processed
 * 2. Only fields that are actually present in the target data are validated
 * 3. All validated fields conform to the schema definition
 *
 * @param params - Object containing validation parameters
 * @param params.schema - Zod object schema defining the structure and validation rules
 * @param params.maskedKeys - Readonly array of schema keys that are allowed to be validated
 * @param params.target - The target data to validate and filter
 * @returns Validated partial object containing only the masked fields that passed validation,
 *          or `undefined` if no valid masked fields are present in the target
 *
 * @example
 * ```typescript
 * // Define what fields to include in the mask
 * const maskedStaffFields = ['firstName', 'email', 'groups'] as const;
 *
 * // Target data with multiple fields
 * const target = {
 *   firstName: 'John',
 *   email: 'john@example.com',
 *   staffId: '123',  // Not in maskedKeys, will be filtered out
 *   invalid: 'test'  // Not in schema, will be filtered out
 * };
 *
 * const validated = maskValidate({
 *   schema: StaffSchema,
 *   maskedKeys: maskedStaffFields,
 *   target
 * });
 * // Returns: { firstName: 'John', email: 'john@example.com' }
 * ```
 *
 * @example
 * ```typescript
 * // When no valid fields are present
 * const validated = maskValidate({
 *   schema: StaffSchema,
 *   maskedKeys: maskedStaffFields,
 *   target: { staffId: '123' }  // staffId is not in the mask
 * });
 * // Returns: undefined
 * ```
 */
export const maskValidate = <
  TSchema extends z.ZodObject<any>,
  TKeys extends readonly (keyof z.infer<TSchema>)[],
>(params: {
  schema: TSchema;
  maskedKeys: TKeys;
  target: Record<string, any>;
}): Simplify<Partial<Pick<z.infer<TSchema>, TKeys[number]>>> | undefined => {
  const maskedSchema = getMaskedSchema(params);

  // If no valid fields to validate, return undefined
  if (!maskedSchema) {
    return undefined;
  }

  return maskedSchema.parse(params.target) as Simplify<
    Partial<Pick<z.infer<TSchema>, TKeys[number]>>
  >;
};

/**
 * Reports what a read tolerated. Values are deliberately left out of the message: they
 * are student data.
 */
const warnOfToleration = (context: string, detail: string) => {
  console.warn(`[validation] Tolerated an invalid ${context}: ${detail}`);
};

const describeError = (error: unknown) =>
  error instanceof Error ? error.message : 'unknown error';

/**
 * Renders an issue path, replacing anything that is not a declared field name — a record
 * key, an array index — with `*`, so a warning names the field that drifted rather than
 * the row it sits in.
 */
const describePath = (schema: z.ZodType, path: readonly PropertyKey[]): string => {
  const segments: string[] = [];
  let current: z.ZodType | undefined = schema;

  for (const key of path) {
    const resolved: z.ZodType | undefined = current ? unwrapSchema(current) : undefined;
    const type: string | undefined = resolved ? (resolved.def as { type: string }).type : undefined;
    const shape: Record<string, z.ZodType> =
      type === 'object' ? ((resolved as z.ZodObject<any>).shape as Record<string, z.ZodType>) : {};

    if (typeof key === 'string' && Object.prototype.hasOwnProperty.call(shape, key)) {
      segments.push(key);
      current = shape[key];
      continue;
    }

    segments.push('*');
    current =
      type === 'array'
        ? (resolved as z.ZodArray<z.ZodType>).element
        : type === 'record'
          ? (resolved as z.ZodRecord<z.ZodString, z.ZodType>).valueType
          : undefined;
  }

  return segments.join('.') || '<root>';
};

// Wrappers whose inner schema may itself be a container worth walking into.
const UNWRAPPABLE_TYPES = new Set([
  'lazy',
  'optional',
  'nullable',
  'default',
  'prefault',
  'nonoptional',
  'readonly',
  'catch',
]);

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const unwrapSchema = (schema: z.ZodType): z.ZodType => {
  const { type } = schema.def as { type: string };
  const unwrap = (schema as { unwrap?: () => z.ZodType }).unwrap;

  return UNWRAPPABLE_TYPES.has(type) && typeof unwrap === 'function'
    ? unwrapSchema(unwrap.call(schema))
    : schema;
};

/**
 * Salvages a value against its schema: whatever validates is parsed, and only the leaves
 * that fail keep what was stored. Objects, arrays and records are walked rather than kept
 * whole, so one bad entry costs neither its siblings' defaults nor the stripping of
 * undeclared keys. A union is kept whole: picking one branch to walk would drop the keys
 * the other branches declare, losing stored data to salvage a schema violation.
 */
const salvageValue = (schema: z.ZodType, value: unknown): unknown => {
  const result = schema.safeParse(value);
  if (result.success) {
    return result.data;
  }

  const resolved = unwrapSchema(schema);
  const { type } = resolved.def as { type: string };

  if (type === 'object' && isPlainObject(value)) {
    const salvaged: Record<string, unknown> = {};

    for (const [field, fieldSchema] of Object.entries(
      (resolved as z.ZodObject<any>).shape as Record<string, z.ZodType>
    )) {
      const stored = Object.prototype.hasOwnProperty.call(value, field);
      const parsed = salvageValue(fieldSchema, value[field]);

      // An absent field contributes only the default the schema supplies for it.
      if (stored || parsed !== undefined) {
        salvaged[field] = parsed;
      }
    }

    return salvaged;
  }

  if (type === 'array' && Array.isArray(value)) {
    const element = (resolved as z.ZodArray<z.ZodType>).element;
    return value.map(entry => salvageValue(element, entry));
  }

  if (type === 'record' && isPlainObject(value)) {
    const { valueType } = resolved as z.ZodRecord<z.ZodString, z.ZodType>;
    return Object.fromEntries(
      Object.entries(value).map(([key, entry]) => [key, salvageValue(valueType, entry)])
    );
  }

  return value;
};

/**
 * Parses without throwing: valid data is parsed as usual, and a value that fails keeps
 * what was stored for it. `suppress` holds issues an earlier pass already reported, so a
 * second pass over the same failure stays quiet.
 */
const lenientParse = <TSchema extends z.ZodObject<any>>(params: {
  schema: TSchema;
  data: unknown;
  context: string;
  suppress?: readonly string[];
}): { data: z.infer<TSchema>; reported: string[] } => {
  const { schema, data, context, suppress = [] } = params;

  // Parsing, naming a failure and salvaging one all reach into the schema's own
  // internals, so a shape they do not expect must still not fail the read.
  try {
    const result = schema.safeParse(data);
    if (result.success) {
      return { data: result.data, reported: [] };
    }

    const issues = [
      ...new Set(
        result.error.issues.map(issue => `${describePath(schema, issue.path)}: ${issue.message}`)
      ),
    ];
    const unreported = issues.filter(issue => !suppress.includes(issue));

    if (unreported.length > 0) {
      warnOfToleration(context, unreported.join('; '));
    }

    return { data: salvageValue(schema, data) as z.infer<TSchema>, reported: issues };
  } catch (error) {
    const failure = `salvage failed (${describeError(error)})`;

    if (!suppress.includes(failure)) {
      warnOfToleration(context, failure);
    }

    return { data: data as z.infer<TSchema>, reported: [failure] };
  }
};

/**
 * `maskValidate` for reads — same masking, tolerant of stored data that no longer
 * matches the schema.
 */
const maskValidateLenient = <TSchema extends z.ZodObject<any>>(params: {
  schema: TSchema;
  maskedKeys: readonly (keyof z.infer<TSchema>)[];
  target: Record<string, any>;
  context: string;
  suppress?: readonly string[];
}): { data: Partial<z.infer<TSchema>>; reported: string[] } | undefined => {
  const maskedSchema = getMaskedSchema({ ...params, declaredOnly: true });

  if (!maskedSchema) {
    return undefined;
  }

  const parsed = lenientParse({
    schema: maskedSchema,
    data: params.target,
    context: params.context,
    ...(params.suppress === undefined ? {} : { suppress: params.suppress }),
  });

  return { data: parsed.data as Partial<z.infer<TSchema>>, reported: parsed.reported };
};

/**
 * Field Encoder/Decoder Utilities
 *
 * These utilities enable bi-directional transformation between client and database formats:
 * - encodeToDb: Transforms client format → DB format (for writes/updates)
 * - decodeFromDb: Transforms DB format → client format (for reads)
 */

/**
 * Helper function for creating a type-safe field transformer.
 *
 * @example
 * ```typescript
 * // Simple array → object transformation
 * const streamsTransformer = createTransformer<
 *   typeof PermissionSchema,
 *   typeof DBPermissionSchema
 * >({
 *   encoder: {
 *     sourceFields: ['streams'],
 *     encode: ({ streams }) => ({
 *       streams: Object.fromEntries(
 *         streams.map(s => [`${s.course}#${s.streamId}`, {}])
 *       ),
 *     }),
 *   },
 *   decoder: {
 *     sourceFields: ['streams'],
 *     decode: ({ streams }) => ({
 *       streams: Object.keys(streams).map(key => {
 *         const [course, streamId] = key.split('#');
 *         return { course, streamId };
 *       }),
 *     }),
 *   },
 * });
 * ```
 */
export function createTransformer<
  TClientSchema extends z.ZodObject<any>,
  TDBSchema extends z.ZodObject<any>,
>(
  transformer: FieldTransformer<TClientSchema, TDBSchema>
): FieldTransformer<TClientSchema, TDBSchema> {
  return transformer;
}

/**
 * A field stored under another name: `clientField` for the API, `dbField` in storage.
 *
 * With `absentAs`, storage holds that placeholder where the client has no value: a stored
 * placeholder reads as absent, and a write of `null` stores the placeholder. With
 * `alwaysStored`, a whole record written without the field stores the placeholder too, for a
 * field storage requires.
 */
export const createRenameTransformer = <
  TClientSchema extends z.ZodObject<any>,
  TDBSchema extends z.ZodObject<any>,
>(
  clientField: Extract<keyof z.infer<TClientSchema>, string>,
  dbField: Extract<keyof z.infer<TDBSchema>, string>,
  options?: { absentAs: string | number; alwaysStored?: boolean }
): FieldTransformer<TClientSchema, TDBSchema> =>
  createTransformer<TClientSchema, TDBSchema>({
    encoder: {
      sourceFields: [clientField] as never,
      encode: data => {
        const value = (data as Record<string, unknown>)[clientField];
        // A partial write runs this only for a field it sets, so `undefined` means a whole record
        if (options && (value === null || (options.alwaysStored && value === undefined))) {
          return { [dbField]: options.absentAs } as never;
        }
        return (value === undefined ? {} : { [dbField]: value }) as never;
      },
    },
    decoder: {
      sourceFields: [dbField] as never,
      decode: data => {
        const value = (data as Record<string, unknown>)[dbField];
        const isAbsent = value === undefined || (options && value === options.absentAs);
        return (isAbsent ? {} : { [clientField]: value }) as never;
      },
    },
  });

/** A field whose storage holds `absentAs` where the client has no value; see `createRenameTransformer`. */
export const createAbsentAsTransformer = <
  TClientSchema extends z.ZodObject<any>,
  TDBSchema extends z.ZodObject<any>,
>(
  field: Extract<keyof z.infer<TClientSchema>, string> & Extract<keyof z.infer<TDBSchema>, string>,
  absentAs: string | number,
  options?: { alwaysStored?: boolean }
): FieldTransformer<TClientSchema, TDBSchema> =>
  createRenameTransformer<TClientSchema, TDBSchema>(field, field, { absentAs, ...options });

/**
 * Creates a function that encodes client data to DB format.
 *
 * @param config - Configuration object containing schemas and transformers
 * @returns A function that encodes client data to DB format
 *
 * @template TClientSchema - The client-side Zod schema
 * @template TDBSchema - The database-side Zod schema
 *
 * @example
 * ```typescript
 * // Create an encoder function
 * const encodePermissionToDb = createEncodeToDbFn(PermissionCodecConfig);
 *
 * // Full encoding (default) - validates input/output, runs all encoders
 * const dbData = encodePermissionToDb({
 *   data: fullPermissionObject,
 * });
 * ```
 *
 * @example
 * ```typescript
 * // Partial encoding - only runs encoders for fields present, no validation
 * const dbData = encodePermissionToDb({
 *   data: { streams: [{ course: 'chem11', streamId: 'abc' }] },
 *   type: 'partial',
 * });
 * // Result: { streams: { 'chem11#abc': {} } }
 * ```
 *
 * @example
 * ```typescript
 * // With existing data for dependencies
 * const dbData = encodePermissionToDb({
 *   data: { active: false },
 *   type: 'partial',
 *   existing: currentRecord,
 * });
 * // Result: { active: 'false', activeBillingRelatedKey: 'false#true' }
 * ```
 *
 * @example
 * ```typescript
 * // With maskedKeys - restricts which fields are validated/encoded during partial encoding.
 * // Only fields in maskedKeys will be processed, even if other fields are present in data.
 * // Defaults to Object.keys(data) if not provided.
 * const dbData = encodePermissionToDb({
 *   data: { streams: [...], permissions: {...}, groups: [...] },
 *   type: 'partial',
 *   maskedKeys: ['streams', 'permissions'],  // 'groups' will be ignored
 * });
 * ```
 */
export const createEncodeToDbFn = <
  TClientSchema extends z.ZodObject<any>,
  TDBSchema extends z.ZodObject<any>,
  TDerived extends Extract<keyof z.infer<TClientSchema>, string> = never,
>(
  config: TransformerConfig<TClientSchema, TDBSchema, TDerived>
): {
  (params: {
    data: EncodeInput<TClientSchema, TDerived>;
    type?: 'full' | undefined;
    existing?: z.infer<TClientSchema> | null;
    maskedKeys?: readonly (keyof z.infer<TClientSchema>)[];
  }): z.infer<TDBSchema>;
  (params: {
    data: PartialEncodeInput<TClientSchema, TDerived> | undefined;
    type: 'partial';
    existing?: z.infer<TClientSchema> | null;
    maskedKeys?: readonly (keyof z.infer<TClientSchema>)[];
  }): Partial<z.infer<TDBSchema>>;
} => {
  const { dbSchema, clientSchema, transformers } = config;
  const derivedFields = Object.keys(config.derived ?? {});
  // `omit` drops a schema's own checks, which a write would then skip
  if (derivedFields.length > 0 && (clientSchema._zod.def.checks?.length ?? 0) > 0) {
    throw new Error(`${config.name}: a client schema with checks of its own cannot derive fields`);
  }
  // A derived field is never stored, so a record is complete without it
  const storedClientSchema =
    derivedFields.length > 0
      ? clientSchema.omit(Object.fromEntries(derivedFields.map(field => [field, true])) as never)
      : clientSchema;

  // Overload signatures
  function encode(params: {
    data: EncodeInput<TClientSchema, TDerived>;
    type?: 'full' | undefined;
    existing?: z.infer<TClientSchema> | null;
    maskedKeys?: readonly (keyof z.infer<TClientSchema>)[];
  }): z.infer<TDBSchema>;

  function encode(params: {
    data: PartialEncodeInput<TClientSchema, TDerived> | undefined;
    type: 'partial';
    existing?: z.infer<TClientSchema> | null;
    maskedKeys?: readonly (keyof z.infer<TClientSchema>)[];
  }): Partial<z.infer<TDBSchema>>;

  // Implementation
  function encode(params: {
    data:
      | EncodeInput<TClientSchema, TDerived>
      | PartialEncodeInput<TClientSchema, TDerived>
      | undefined;
    type?: 'full' | 'partial' | undefined;
    existing?: z.infer<TClientSchema> | null;
    maskedKeys?: readonly (keyof z.infer<TClientSchema>)[];
  }): Partial<z.infer<TDBSchema>> | z.infer<TDBSchema> {
    const { data, type = 'full', existing = null } = params;

    // Skip if data is undefined
    if (!data) {
      return type === 'full' ? dbSchema.parse({}) : {};
    }

    // Validate input data
    let validatedData;
    if (type === 'full') {
      validatedData = storedClientSchema.parse(data) as z.infer<TClientSchema>;
    } else {
      // A key left `undefined` was not set, so it must not take its field's default
      const setData = Object.fromEntries(
        Object.entries(data as Record<string, unknown>).filter(
          ([field, value]) => value !== undefined && !derivedFields.includes(field)
        )
      );
      validatedData = maskValidate({
        schema: clientSchema,
        maskedKeys: (params.maskedKeys ??
          Object.keys(setData)) as readonly (keyof z.infer<TClientSchema>)[],
        target: setData,
      });

      if (!validatedData) {
        return {};
      }
    }

    // Get all encoders
    const allEncoders = transformers.map(t => t.encoder).filter(encoder => encoder !== undefined);

    // Determine which encoders to run based on type
    const encodersToRun =
      type === 'full'
        ? allEncoders
        : allEncoders.filter(encoder => encoder.sourceFields.some(field => field in validatedData));

    // Collect all fields that are handled by encoders
    const handledFields = new Set<string>();
    for (const encoder of allEncoders) {
      for (const field of encoder.sourceFields) {
        handledFields.add(field as string);
      }
    }

    // Passthrough fields: fields in validatedData that are not handled by any encoder
    const passthroughData: Record<string, unknown> = {};
    for (const field of Object.keys(validatedData)) {
      if (!handledFields.has(field)) {
        passthroughData[field] = validatedData[field];
      }
    }

    // Run encoders
    const encodedParts = encodersToRun.map(encoder => {
      // Extract relevant fields from validatedData
      const sourceData: Record<string, unknown> = Object.fromEntries(
        encoder.sourceFields
          .filter(field => field in validatedData)
          .map(field => [field, validatedData[field]])
      );

      // Add dependencies from existing data if needed
      if (encoder.dependencies) {
        encoder.dependencies.forEach(depField => {
          if (!(depField in sourceData)) {
            if (existing?.[depField] === undefined) {
              throw new Error('Missing dependency field in existing data: ' + String(depField));
            }

            sourceData[depField as string] = (existing as Record<string, unknown>)[
              depField as string
            ];
          }
        });
      }
      // Run encoder
      return encoder.encode(sourceData as any, existing, { type });
    });

    // Merge passthrough data with encoded parts
    const result = Object.assign({}, passthroughData, ...encodedParts);

    // Never write back a dependency the caller didn't supply: it's unchanged,
    // and may be a read-only key that ElectroDB rejects in a patch.
    if (type === 'partial') {
      const provided = new Set(Object.keys(validatedData));
      for (const encoder of encodersToRun) {
        for (const dependency of encoder.dependencies ?? []) {
          if (!provided.has(dependency as string)) {
            delete (result as Record<string, unknown>)[dependency as string];
          }
        }
      }
    }

    // For full mode, validate output against DB schema
    return type === 'full'
      ? (dbSchema.parse(result) as z.infer<TDBSchema>)
      : (maskValidate({
          schema: dbSchema,
          maskedKeys: Object.keys(result) as (keyof z.infer<TDBSchema>)[],
          target: result,
        }) ?? {});
  }

  return encode;
};

/**
 * Creates a function that decodes DB data to client format.
 *
 * This function:
 * 1. Determines which decoders need to run based on type and fields present in input
 * 2. Runs decoders in parallel
 * 3. Merges decoded results into a single object
 *
 * Validation here never throws. A stored value that no longer matches its schema is
 * kept and logged, so one row that has drifted cannot fail the read that returns it.
 *
 * @param config - Configuration object containing schemas and transformers
 * @returns A function that decodes DB data to client format
 *
 * @template TClientSchema - The client-side Zod schema
 * @template TDBSchema - The database-side Zod schema
 *
 * @example
 * ```typescript
 * // Create a decoder function
 * const decodePermissionFromDb = createDecodeFromDbFn(PermissionCodecConfig);
 *
 * // Full decoding (default) - validates input/output, runs all decoders
 * const clientData = decodePermissionFromDb({
 *   data: fullDBPermissionObject,
 * });
 * ```
 *
 * @example
 * ```typescript
 * // Partial decoding - only runs decoders for fields present, no validation
 * const clientData = decodePermissionFromDb({
 *   data: { streams: { 'chem11#abc': {}, 'phys12#def': {} } },
 *   type: 'partial',
 * });
 * // Result: { streams: [{ course: 'chem11', streamId: 'abc' }, { course: 'phys12', streamId: 'def' }] }
 * ```
 *
 * @example
 * ```typescript
 * // With maskedKeys - restricts which fields are validated/decoded during partial decoding.
 * // Only fields in maskedKeys will be processed, even if other fields are present in data.
 * // Defaults to Object.keys(data) if not provided.
 * const clientData = decodePermissionFromDb({
 *   data: { streams: {...}, permissions: {...}, groups: {...} },
 *   type: 'partial',
 *   maskedKeys: ['streams', 'permissions'],  // 'groups' will be ignored
 * });
 * ```
 */
export const createDecodeFromDbFn = <
  TClientSchema extends z.ZodObject<any>,
  TDBSchema extends z.ZodObject<any>,
  TDerived extends Extract<keyof z.infer<TClientSchema>, string> = never,
>(
  config: TransformerConfig<TClientSchema, TDBSchema, TDerived>
): {
  (params: { data: z.input<TDBSchema>; type?: 'full' | undefined }): z.infer<TClientSchema>;
  (params: { data: Partial<z.input<TDBSchema>>; type: 'partial' }): Partial<z.infer<TClientSchema>>;
} => {
  const { name, clientSchema, dbSchema, transformers } = config;

  const storedContext = `${name} row read from the database`;
  const decodedContext = `${name} decoded from the database`;

  // Overload signatures
  function decode(params: {
    data: z.input<TDBSchema>;
    type?: 'full' | undefined;
    maskedKeys?: readonly (keyof z.infer<TDBSchema>)[];
  }): z.infer<TClientSchema>;

  function decode(params: {
    data: Partial<z.input<TDBSchema>> | undefined;
    type: 'partial';
    maskedKeys?: readonly (keyof z.infer<TDBSchema>)[];
  }): Partial<z.infer<TClientSchema>>;

  // Implementation
  function decode(params: {
    data: Partial<z.input<TDBSchema>> | z.input<TDBSchema> | undefined;
    type?: 'full' | 'partial' | undefined;
    maskedKeys?: readonly (keyof z.infer<TDBSchema>)[];
  }): Partial<z.infer<TClientSchema>> | z.infer<TClientSchema> {
    const { data, type = 'full' } = params;

    // Skip if data is undefined
    if (!data) {
      return type === 'full'
        ? lenientParse({ schema: clientSchema, data: {}, context: decodedContext }).data
        : {};
    }

    // Validate input data
    let validatedData;
    let storedIssues: string[];
    if (type === 'full') {
      const parsed = lenientParse({ schema: dbSchema, data, context: storedContext });
      validatedData = parsed.data;
      storedIssues = parsed.reported;
    } else {
      const parsed = maskValidateLenient({
        schema: dbSchema,
        maskedKeys: (params.maskedKeys ?? Object.keys(data)) as (keyof z.infer<TDBSchema>)[],
        target: data,
        context: storedContext,
      });

      if (!parsed) {
        return {};
      }

      validatedData = parsed.data;
      storedIssues = parsed.reported;
    }

    // Get all decoders
    const allDecoders = transformers.map(t => t.decoder).filter(decoder => decoder !== undefined);

    // Determine which decoders to run based on type
    const decodersToRun =
      type === 'full'
        ? allDecoders
        : allDecoders.filter(decoder => decoder.sourceFields.some(field => field in validatedData));

    // Collect all fields that are handled by decoders
    const handledFields = new Set<string>();
    for (const decoder of allDecoders) {
      for (const field of decoder.sourceFields) {
        handledFields.add(field as string);
      }
    }

    // Passthrough fields: fields in validatedData that are not handled by any decoder
    const passthroughData: Record<string, unknown> = {};
    for (const field of Object.keys(validatedData)) {
      if (!handledFields.has(field)) {
        passthroughData[field] = validatedData[field];
      }
    }

    // Run decoders
    const decodedParts = decodersToRun.map(decoder => {
      // Extract relevant fields from validatedData
      const sourceData: Record<string, unknown> = Object.fromEntries(
        decoder.sourceFields
          .filter(field => field in validatedData)
          .map(field => [field, validatedData[field]])
      );

      // A decoder is written against a row that validated, so one whose input has
      // drifted away can throw. A read must survive that: drop what the decoder would
      // have contributed and let the client schema supply what it can.
      try {
        return decoder.decode(sourceData as any);
      } catch (error) {
        console.warn(
          `[validation] Skipped a failing ${name} decoder for ${decoder.sourceFields.join(
            ', '
          )}: ${describeError(error)}`
        );
        return {};
      }
    });

    // Merge passthrough data with decoded parts
    const result = Object.assign({}, passthroughData, ...decodedParts);

    for (const [field, build] of Object.entries(config.derived ?? {}) as [
      string,
      (record: Partial<z.infer<TClientSchema>>) => unknown,
    ][]) {
      // A read must survive a build that throws, as it does a decoder
      try {
        const value = build(result);
        if (value !== undefined) {
          result[field] = value;
        }
      } catch (error) {
        console.warn(
          `[validation] Skipped a failing ${name} build of ${field}: ${describeError(error)}`
        );
      }
    }

    // For full mode, validate output against client schema. A row already reported as
    // stored-invalid decodes into a shape that fails for the same reason, so only what
    // the stored row did not already account for is worth saying again.
    return type === 'full'
      ? (lenientParse({
          schema: clientSchema,
          data: result,
          context: decodedContext,
          suppress: storedIssues,
        }).data as z.infer<TClientSchema>)
      : (maskValidateLenient({
          schema: clientSchema,
          maskedKeys: Object.keys(result) as (keyof z.infer<TClientSchema>)[],
          target: result,
          context: decodedContext,
          suppress: storedIssues,
        })?.data ?? {});
  }

  return decode;
};
